import {Text} from '#component/base';

const Reference = ({number = 0}) => (
  <Text as="a" color="eggplantDark" href="#references">
    {` [${number}] `}
  </Text>
);

export const links = [
  {
    value: 'overview',
    label: 'Overview',
  },
  {
    value: 'terminology',
    label: 'Terminology',
  },
  {
    value: 'how-it-works',
    label: 'How it works',
  },
  {
    value: 'lifestyle-factors',
    label: 'Lifestyle Factors',
  },
  {
    value: 'learn',
    label: 'Learn More',
  },
];

export const infoHeader = {
  title: 'Understanding your TallyAge™',
  firstParagraph:
    'A TallyAge™ is a window into your epigenetic age at a given time, based on the methylation patterns of your DNA.',
  secondParagraph:
    'Our team of scientists have trained a state of the art computational model using a combination of lifestyle information and genome-wide methylation measurements from a diverse population of over 8,000 people. The computational model predicts your TallyAge (TM) using thousands of methylation sites that are sensitive to aging and lifestyle.',
  thirdParagraph:
    'Different from chronological age, which is static, a TallyAge™ can change over time. Lifestyle, environment, and stressors to hormonal imbalances and medical history may influence epigenetic age.',
  highlightedText: 'Regardless of your result today, you have the control to change the future.',
  showInfoCard: true,
  imageUrl: '/image/tally-explorer/test-tubes.png',
};

export const terminology = {
  title: 'Terminology',
  body: 'Whether you’re a biohacker or new to this space, we’ve got you covered. Below is a list of common terminology that can help make sense of our process.',
  items: [
    {
      title: 'Cohort',
      copy: (
        <p>
          Since aging changes as we age and is different between males and females, it is useful to
          compare yourself to others like you. Your cohort is made up of other Tally members with
          about the same chronological age as you. When you specify your birth sex, we filter your
          cohort to just those of your specified sex. Cohorts are used to give you the most accurate
          lifestyle recommendations and Action plans, customized to you.
        </p>
      ),
    },
    {
      header: {
        height: 300,
        src: '/image/understanding-results/cpg.svg',
        width: 654,
      },
      title: 'CpG Site',
      copy: (
        <p>
          Your genome is a long DNA molecule with over 2 billion A, C, G, and T bases. When a C is
          followed by a G, a CpG, the C can be decorated with a chemical methyl group, and these
          groups help regulate which genes are turned on. Our test measures the average methylation
          status of thousands of CpGs in your genome.
        </p>
      ),
    },
    {
      title: 'Epigenetic clock',
      copy: (
        <p>
          An epigenetic clock is a computational model that combines methylation levels at specific
          sites in your genome into an accurate estimate of your epigenetic age. Our TallyAge (TM)
          clock is sensitive to lifestyle factors such as sleep, diet, exercise, and alcohol
          consumption, so your lifestyle plays an important role in your estimated epigenetic age.
        </p>
      ),
    },
    {
      header: {
        height: 300,
        src: '/image/understanding-results/methylation.svg',
        width: 654,
      },
      title: 'Methylation',
      copy: (
        <>
          <p>
            DNA methylation is a biological regulatory process that uses a molecule known as a
            methyl group to mark specific regions of DNA known as CpG sites.
          </p>
          <p>
            Age and age-related diseases are strongly correlated with changes in DNA methylation
            <Reference number={1} />. DNA methylation can influence gene expression, and specific
            CpG sites can be used to predict epigenetic age.
            <Reference number={2} />
          </p>
          <p>
            DNA methylation patterns change with age, with some areas becoming hypomethylated
            (decreased methylation) and others becoming hypermethylated (increased methylation).
          </p>
          <p>
            Through methylation changes in the genome, we can also see how improvements in
            lifestyle, such as exercise, eating a plant-rich diet or managing stress, change
            epigenetic patterns and methylation for the better <Reference number={3} />.
          </p>
        </>
      ),
    },
  ],
};

export const infoCTAData = {
  title: 'Surprised by your results?',
  paragraph:
    'There are many factors that influence how someone ages on an epigenetic level, from environment and stressors to hormonal imbalances and medical history.',
  buttonText: 'Explore possible factors',
  imageUrl: '/image/tally-explorer/couple-ss.png',
  href: '/surprisedByYourResults',
};

export const references = [
  {
    authors: 'Kane, A. E., & Sinclair, D. A. (2019)',
    title:
      'Epigenetic changes during aging and their reprogramming potential. Crit Rev Biochem Mol Biol, 54(1), 61-83.',
    url: 'https://www.tandfonline.com/doi/abs/10.1080/10409238.2019.1570075?journalCode=ibmg20',
  },
  {
    authors:
      'Levine, M. E., Lu, A. T., Quach, A., Chen, B. H., Assimes, T. L., Bandinelli, S., Hou, L., Baccarelli, A. A., Stewart, J. D., … & Horvath, S. (2018).',
    title: 'An epigenetic biomarker of aging for lifespan and healthspan. Aging, 10(4), 573-591.',
    url: 'https://www.aging-us.com/article/101414/text',
  },
  {
    authors: 'Kim, K., Zheng, Y., Joyce, B.T., … & Hou, L (2022).',
    title:
      'Relative contributions of six lifestyle- and health-related exposures to epigenetic aging: the Coronary Artery Risk Development in Young Adults (CARDIA) Study. Clin Epigenet, 14(85), 1-12.',
    url: 'https://clinicalepigeneticsjournal.biomedcentral.com/articles/10.1186/s13148-022-01304-9',
  },
  {
    authors: 'Johnson, A.A., English, B.W., Shokhirev, M.N., Sinclair, D.A., Cuellar, T.L. (2022).',
    title: 'Human  age reversal: Fact or fiction? Aging Cell, 21(8), :e13664.',
    url: 'https://onlinelibrary.wiley.com/doi/10.1111/acel.13664',
  },
  {
    authors:
      'Ruby, J. G., Wright, K. M., Rand, K. A., Kermany, A., Noto, K., Curtis, D., Varner, N., … & Pritchard, J. K. (2018).',
    title:
      'Estimates of the heritability of human longevity are substantially inflated due to assortative mating. Genetics, 210(3), 1109–1124.',
    url: 'https://academic.oup.com/genetics/article/210/3/1109/5931081',
  },
  {
    authors:
      'Fitzgerald, K. N., Hodges, R., Hanes, D., Stack, E., Cheishvili, D., Szyf, M., Henkel, J., Twedt, M. W., … & Bradley, R. (2021).',
    title:
      'Potential reversal of epigenetic age using a diet and lifestyle intervention: A pilot randomized clinical trial. Aging, 13(7), 9419–9432.',
    url: 'https://www.aging-us.com/article/202913/text',
  },
];
